import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * Portfolio-scoped CC/CSP lens.
 *
 * Deliberately SEPARATE from the screener's StrategyContext (cc | csp). The
 * screener lens means "what am I shopping for" — one strategy at a time. The
 * portfolio lens means "what am I looking at across positions I already hold" —
 * so it adds an 'all' option and defaults to it. Keeping them independent
 * avoids the two pages fighting over one URL param / localStorage key.
 */
export type PortfolioLens = 'all' | 'cc' | 'csp';

const STORAGE_KEY = 'portfolioLens';

interface PortfolioLensContextType {
  lens: PortfolioLens;
  setLens: (l: PortfolioLens) => void;
  /** True when a position's position_type should be shown under the current lens. */
  matchesLens: (positionType?: string | null) => boolean;
}

const PortfolioLensContext = createContext<PortfolioLensContextType>({
  lens: 'all',
  setLens: () => {},
  matchesLens: () => true,
});

export const usePortfolioLens = () => useContext(PortfolioLensContext);

/** Pure helper so non-React code (sorts, filters) can reuse the same rule. */
export const positionMatchesLens = (lens: PortfolioLens, positionType?: string | null): boolean => {
  if (lens === 'all') return true;
  const isCsp = positionType === 'cash_secured_put';
  return lens === 'csp' ? isCsp : !isCsp;
};

export const PortfolioLensProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lens, setLensState] = useState<PortfolioLens>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'all' || stored === 'cc' || stored === 'csp') return stored;
    return 'all';
  });

  const setLens = useCallback((l: PortfolioLens) => {
    setLensState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const matchesLens = useCallback(
    (positionType?: string | null) => positionMatchesLens(lens, positionType),
    [lens]
  );

  return (
    <PortfolioLensContext.Provider value={{ lens, setLens, matchesLens }}>
      {children}
    </PortfolioLensContext.Provider>
  );
};
